import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import { cn } from "./cn";

/**
 * Small, plain components. Large tap targets and visible focus rings for phones and for people
 * who do not use software all day (REQUIREMENTS "non-negotiable principle", NFR-8).
 */

type Variant = "primary" | "secondary" | "danger" | "ghost";

const variantClass: Record<Variant, string> = {
  primary: "bg-brand-600 text-white hover:bg-brand-700 focus-visible:outline-brand-600",
  secondary:
    "bg-surface text-ink border border-line hover:bg-brand-50 focus-visible:outline-brand-600",
  danger: "bg-danger text-white hover:opacity-90 focus-visible:outline-danger",
  ghost: "bg-transparent text-brand-600 hover:bg-brand-50 focus-visible:outline-brand-600",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  busy?: boolean;
}

export function Button({
  variant = "primary",
  busy,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cn(
        "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 text-base font-medium",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
        variantClass[variant],
        className,
      )}
    >
      {busy ? <Spinner size="sm" /> : null}
      {children}
    </button>
  );
}

export function Label({ className, ...rest }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label {...rest} className={cn("mb-1 block text-sm font-medium text-ink", className)} />;
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      className={cn(
        "block min-h-11 w-full rounded-lg border border-line bg-surface px-3 text-base text-ink",
        "focus:border-brand-500 focus:outline focus:outline-2 focus:outline-brand-500/30",
        className,
      )}
    />
  );
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...rest}
      className={cn(
        "block min-h-11 w-full rounded-lg border border-line bg-surface px-3 text-base text-ink",
        "focus:border-brand-500 focus:outline focus:outline-2 focus:outline-brand-500/30",
        className,
      )}
    >
      {children}
    </select>
  );
}

export interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}

export function Field({ id, label, hint, error, children }: FieldProps) {
  return (
    <div className="mb-4">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && !error ? <p className="mt-1 text-sm text-muted">{hint}</p> : null}
      {error ? (
        <p className="mt-1 text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function Card({
  className,
  children,
  title,
}: {
  className?: string;
  children: ReactNode;
  title?: string;
}) {
  return (
    <section className={cn("rounded-card border border-line bg-surface p-5 shadow-sm", className)}>
      {title ? <h2 className="mb-3 text-lg font-semibold text-ink">{title}</h2> : null}
      {children}
    </section>
  );
}

export function Banner({
  tone = "info",
  children,
}: {
  tone?: "info" | "ok" | "warn" | "danger";
  children: ReactNode;
}) {
  const tones = {
    info: "border-brand-100 bg-brand-50 text-brand-700",
    ok: "border-ok/30 bg-ok/10 text-ok",
    warn: "border-warn/30 bg-warn/10 text-warn",
    danger: "border-danger/30 bg-danger/10 text-danger",
  };
  return (
    <div role="status" className={cn("mb-4 rounded-lg border px-4 py-3 text-sm", tones[tone])}>
      {children}
    </div>
  );
}

export function Spinner({ size = "md" }: { size?: "sm" | "md" }) {
  const dim = size === "sm" ? "h-4 w-4" : "h-8 w-8";
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-block animate-spin rounded-full border-2 border-current border-r-transparent",
        dim,
      )}
    />
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-card border border-dashed border-line p-8 text-center">
      <p className="text-base font-medium text-ink">{title}</p>
      {body ? <p className="mt-1 text-sm text-muted">{body}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("overflow-x-auto rounded-card border border-line", className)}>
      <table className="w-full text-left text-sm">{children}</table>
    </div>
  );
}

export function Th({ children }: { children: ReactNode }) {
  return (
    <th className="border-b border-line bg-canvas px-3 py-2 font-medium text-muted">{children}</th>
  );
}

export function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn("border-b border-line px-3 py-3 text-ink", className)}>{children}</td>;
}
